import { useRef, useEffect, useState, useMemo } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";

const CITIES = [
  { lat: 41.0082, lng: 28.9784 }, // Istanbul
  { lat: 51.5074, lng: -0.1278 }, // London
  { lat: 40.7128, lng: -74.006 }, // NYC
  { lat: 35.6762, lng: 139.6503 }, // Tokyo
  { lat: 48.8566, lng: 2.3522 }, // Paris
  { lat: -23.5505, lng: -46.6333 }, // Sao Paulo
  { lat: 19.076, lng: 72.8777 }, // Mumbai
  { lat: -33.8688, lng: 151.2093 }, // Sydney
  { lat: 25.2048, lng: 55.2708 }, // Dubai
  { lat: 37.7749, lng: -122.4194 }, // SF
  { lat: 55.7558, lng: 37.6173 }, // Moscow
  { lat: 1.3521, lng: 103.8198 }, // Singapore
];

const BRAND = ["#FF2A85", "#D800C3", "#8A2BE2", "#0055FF"];

export const AuraGlobe = () => {
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const [size, setSize] = useState(460);
  const [webgl] = useState(() => {
    try {
      const c = document.createElement("canvas");
      return !!(
        window.WebGLRenderingContext &&
        (c.getContext("webgl") || c.getContext("experimental-webgl"))
      );
    } catch (e) {
      return false;
    }
  });
  const [countries, setCountries] = useState({ features: [] });

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth;
      const vw = window.innerWidth - 48;
      setSize(Math.max(240, Math.min(w, vw, 560)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(
      "https://raw.githubusercontent.com/vasturiano/react-globe.gl/master/example/datasets/ne_110m_admin_0_countries.geojson"
    )
      .then((r) => r.json())
      .then((d) => alive && setCountries(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    g.controls().autoRotate = true;
    g.controls().autoRotateSpeed = 0.7;
    g.controls().enableZoom = false;
    g.pointOfView({ lat: 20, lng: 10, altitude: 2.2 }, 0);
  }, [size, countries]);

  const arcs = useMemo(() => {
    const a = [];
    for (let i = 0; i < 14; i++) {
      const s = CITIES[Math.floor(Math.random() * CITIES.length)];
      const t = CITIES[Math.floor(Math.random() * CITIES.length)];
      a.push({ startLat: s.lat, startLng: s.lng, endLat: t.lat, endLng: t.lng, c: BRAND[i % BRAND.length] });
    }
    return a;
  }, []);

  const globeMaterial = useMemo(
    () => new THREE.MeshPhongMaterial({ color: "#ffffff", transparent: true, opacity: 0.9 }),
    []
  );

  return (
    <div ref={wrapRef} className="relative flex w-full items-center justify-center" data-testid="aura-globe">
      {/* fallback / ambient glow */}
      <div
        className="pointer-events-none absolute aspect-square w-[80%] rounded-full opacity-40 blur-3xl"
        style={{ background: "var(--grad-brand)" }}
      />
      {!webgl && (
        <div
          className="relative aspect-square w-[74%] max-w-[420px] rounded-full opacity-90 shadow-2xl"
          style={{ background: "var(--grad-brand)" }}
          data-testid="aura-globe-fallback"
        />
      )}
      {webgl && (
      <Globe
        ref={globeRef}
        width={size}
        height={size}
        backgroundColor="rgba(0,0,0,0)"
        globeMaterial={globeMaterial}
        showAtmosphere
        atmosphereColor="#FF2A85"
        atmosphereAltitude={0.16}
        hexPolygonsData={countries.features}
        hexPolygonResolution={3}
        hexPolygonMargin={0.55}
        hexPolygonUseDots={true}
        hexPolygonColor={(_, i) => BRAND[i % BRAND.length]}
        arcsData={arcs}
        arcColor={(d) => [d.c, "#0055FF"]}
        arcAltitude={0.22}
        arcStroke={0.55}
        arcDashLength={0.4}
        arcDashGap={0.25}
        arcDashInitialGap={() => Math.random()}
        arcDashAnimateTime={2600}
        pointsData={CITIES}
        pointLat="lat"
        pointLng="lng"
        pointColor={() => "#FF2A85"}
        pointAltitude={0.01}
        pointRadius={0.32}
      />
      )}
    </div>
  );
};

export default AuraGlobe;
